/**
 * ScenarioPage — /agentic-interviewing/scenario/:slug
 *
 * Full system design scenario page from the Phase 4 framework document.
 * Each of the 6 scenarios has its own standalone URL and renders the
 * complete evaluation guide:
 *   - Scenario prompt
 *   - Strong clarifying questions
 *   - Target architecture (pattern + rationale + components)
 *   - Design pattern discussion
 *   - 3 key trade-offs with interviewer insight
 *   - Observability & evaluation strategy
 *   - Scalability & cost analysis
 *   - Safety & governance considerations
 *   - Level differentiation guide (Intermediate → Principal)
 *
 * DUAL AUDIENCE
 * ─────────────
 * Candidate view: framed as "how to approach this scenario"
 *   - Clarifying questions = "ask these before designing"
 *   - Trade-offs = "choices you'll need to make and defend"
 *   - Level guide = "what scope you should demonstrate at your target level"
 *
 * Hiring Manager view: framed as "how to evaluate this scenario"
 *   - Clarifying questions = "strong candidates surface these naturally"
 *   - Trade-offs = "force a choice, evaluate the reasoning"
 *   - Level guide = "calibrate response against level expectations"
 */

import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonIcon from "@mui/icons-material/Person";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import InsightsIcon from "@mui/icons-material/Insights";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import GradeIcon from "@mui/icons-material/Grade";
import TuneIcon from "@mui/icons-material/Tune";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getScenarioBySlug,
  PHASE4_SCENARIOS,
  type Phase4Scenario,
} from "../data/phase4Scenarios";
import { L } from "../utils/localized";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Audience = "candidate" | "hiringManager";

// ---------------------------------------------------------------------------
// Reusable section wrapper with icon + heading
// ---------------------------------------------------------------------------

function Section({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  const theme = useTheme();
  const levelColor = theme.palette.warning.main;
  return (
    <Box mb={5}>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={subtitle ? 0.5 : 2}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(levelColor, 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: levelColor,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" fontWeight={500}>
          {title}
        </Typography>
      </Stack>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 6.25 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Renders multi-paragraph prose (split on \n\n, handles bullet lines starting with •)
// ---------------------------------------------------------------------------

function Prose({ text }: { text: string }) {
  const theme = useTheme();
  const levelColor = theme.palette.warning.main;
  const paragraphs = text.split("\n\n");
  return (
    <>
      {paragraphs.map((para, i) => {
        // Check if paragraph is a bulleted list
        if (para.split("\n").some((line) => line.trim().startsWith("•"))) {
          const lines = para.split("\n");
          return (
            <Box key={i} sx={{ mb: 2 }}>
              {lines.map((line, j) => {
                const trimmed = line.trim();
                if (trimmed.startsWith("•")) {
                  return (
                    <Stack key={j} direction="row" spacing={1} alignItems="flex-start" mb={0.75}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: levelColor,
                          mt: "9px",
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                        {trimmed.slice(1).trim()}
                      </Typography>
                    </Stack>
                  );
                }
                return trimmed ? (
                  <Typography
                    key={j}
                    variant="body2"
                    sx={{ mb: 1, lineHeight: 1.75 }}
                  >
                    {trimmed}
                  </Typography>
                ) : null;
              })}
            </Box>
          );
        }
        return (
          <Typography
            key={i}
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.75, color: "text.primary" }}
          >
            {para}
          </Typography>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ScenarioPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation("pages-account");
  const levelColor = theme.palette.warning.main;
  const [audience, setAudience] = useState<Audience>("candidate");

  const scenarios = L(PHASE4_SCENARIOS, i18n.language);
  const scenario: Phase4Scenario | undefined = slug
    ? getScenarioBySlug(slug, i18n.language)
    : undefined;

  if (!scenario) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h5">{t("ScenarioPage.notFoundHeading")}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
          {t("ScenarioPage.availableScenarios", { list: scenarios.map((s) => s.title).join(", ") })}
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/agentic-interviewing/level/4")}
        >
          {t("ScenarioPage.backToLevel4Button")}
        </Button>
      </Container>
    );
  }

  // Find adjacent scenarios for footer navigation
  const currentIndex = scenarios.findIndex((s) => s.slug === scenario.slug);
  const prevScenario = currentIndex > 0 ? scenarios[currentIndex - 1] : null;
  const nextScenario =
    currentIndex < scenarios.length - 1 ? scenarios[currentIndex + 1] : null;

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>

      {/* ── Hero header ─────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(levelColor, 0.12)} 0%, ${alpha(levelColor, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(levelColor, 0.2)}`,
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/agentic-interviewing/level/4")}
              size="small"
              sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}
            >
              {t("ScenarioPage.level4Button")}
            </Button>
            <Typography variant="body2" color="text.disabled">›</Typography>
            <Typography variant="body2" color="text.secondary">{t("ScenarioPage.scenarioCounter", { current: currentIndex + 1, total: scenarios.length })}</Typography>
          </Stack>

          <Stack direction="row" spacing={1} mb={1.5} alignItems="center" flexWrap="wrap" gap={0.5}>
            <Chip
              label={t("ScenarioPage.systemDesignChip")}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.72rem",
                letterSpacing: "0.07em",
                bgcolor: alpha(levelColor, 0.12),
                color: levelColor,
              }}
            />
            <Chip
              label={scenario.complexityLabel}
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.7rem",
                borderColor: alpha(levelColor, 0.3),
                color: "text.secondary",
              }}
            />
            <Chip
              label={scenario.industry}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", color: "text.secondary" }}
            />
          </Stack>
          <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 0.5 }}>
            {scenario.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            {t("ScenarioPage.targetLevels", { levels: scenario.targetLevels })}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>

        {/* ── Audience toggle ──────────────────────────── */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
          sx={{ pb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {t("ScenarioPage.viewingAsLabel")}
          </Typography>
          <ToggleButtonGroup
            value={audience}
            exclusive
            onChange={(_, v) => v && setAudience(v as Audience)}
            size="small"
          >
            <ToggleButton
              value="candidate"
              sx={{
                px: 2,
                fontSize: "0.8rem",
                fontWeight: 500,
                gap: 0.75,
                "&.Mui-selected": {
                  bgcolor: alpha(levelColor, 0.12),
                  color: levelColor,
                  borderColor: alpha(levelColor, 0.3),
                },
              }}
            >
              <PersonIcon sx={{ fontSize: 16 }} />
              {t("ScenarioPage.candidateToggle")}
            </ToggleButton>
            <ToggleButton
              value="hiringManager"
              sx={{
                px: 2,
                fontSize: "0.8rem",
                fontWeight: 500,
                gap: 0.75,
                "&.Mui-selected": {
                  bgcolor: alpha(levelColor, 0.12),
                  color: levelColor,
                  borderColor: alpha(levelColor, 0.3),
                },
              }}
            >
              <BusinessCenterIcon sx={{ fontSize: 16 }} />
              {t("ScenarioPage.hiringManagerToggle")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* ── Scenario prompt ──────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: alpha(levelColor, 0.04),
            border: `1px solid ${alpha(levelColor, 0.25)}`,
            borderRadius: 2,
            p: 3,
            mb: 5,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: levelColor, letterSpacing: "0.04em", textTransform: "uppercase" }}
          >
            {t("ScenarioPage.scenarioPromptLabel")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, lineHeight: 1.8, color: "text.primary" }}>
            {scenario.prompt}
          </Typography>
        </Paper>

        {/* ── Clarifying questions ─────────────────────── */}
        <Section
          icon={<HelpOutlineIcon sx={{ fontSize: 20 }} />}
          title={t("ScenarioPage.clarifyingQuestionsTitle")}
          subtitle={
            audience === "candidate"
              ? t("ScenarioPage.clarifyingQuestionsSubtitleCandidate")
              : t("ScenarioPage.clarifyingQuestionsSubtitleHm")
          }
        >
          <Stack spacing={1.5}>
            {scenario.clarifyingQuestions.map((q, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderLeft: `3px solid ${alpha(levelColor, 0.5)}`,
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                  {q.question}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  {q.purpose}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* ── Target architecture ──────────────────────── */}
        <Section
          icon={<AccountTreeIcon sx={{ fontSize: 20 }} />}
          title={t("ScenarioPage.targetArchitectureTitle")}
        >
          <Paper
            elevation={0}
            sx={{
              bgcolor: alpha(levelColor, 0.04),
              border: `1px solid ${alpha(levelColor, 0.25)}`,
              borderRadius: 2,
              p: 2.5,
              mb: 2,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: levelColor, letterSpacing: "0.04em", textTransform: "uppercase" }}
            >
              {t("ScenarioPage.patternLabel")}
            </Typography>
            <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5, mb: 1.5 }}>
              {scenario.targetArchitecture.pattern}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.7, color: "text.secondary" }}>
              {scenario.targetArchitecture.rationale}
            </Typography>
          </Paper>

          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color: "text.secondary",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              display: "block",
              mb: 1.5,
            }}
          >
            {t("ScenarioPage.keyComponentsLabel")}
          </Typography>
          <Stack spacing={1}>
            {scenario.targetArchitecture.keyComponents.map((c, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 1.75,
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="flex-start">
                  <Typography variant="body2" fontWeight={500} sx={{ minWidth: { sm: 180 } }}>
                    {c.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    {c.description}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* ── Design pattern discussion ────────────────── */}
        <Section
          icon={<InsightsIcon sx={{ fontSize: 20 }} />}
          title={t("ScenarioPage.designPatternDiscussionTitle")}
        >
          <Prose text={scenario.designPatternDiscussion} />
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* ── Key trade-offs ───────────────────────────── */}
        <Section
          icon={<CompareArrowsIcon sx={{ fontSize: 20 }} />}
          title={t("ScenarioPage.keyTradeOffsTitle")}
          subtitle={
            audience === "candidate"
              ? t("ScenarioPage.keyTradeOffsSubtitleCandidate")
              : t("ScenarioPage.keyTradeOffsSubtitleHm")
          }
        >
          <Stack spacing={3}>
            {scenario.tradeOffs.map((tradeOff, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2.5,
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ color: "text.disabled", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                  {t("ScenarioPage.tradeOffLabel", { index: i + 1 })}
                </Typography>
                <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5, mb: 2 }}>
                  {tradeOff.title}
                </Typography>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      bgcolor: alpha(theme.palette.info.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                      borderRadius: 2,
                      p: 1.75,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: theme.palette.info.dark, letterSpacing: "0.04em", textTransform: "uppercase" }}
                    >
                      {t("ScenarioPage.optionALabel", { label: tradeOff.optionA.label })}
                    </Typography>
                    <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                      {tradeOff.optionA.description}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                      borderRadius: 2,
                      p: 1.75,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: theme.palette.primary.main, letterSpacing: "0.04em", textTransform: "uppercase" }}
                    >
                      {t("ScenarioPage.optionBLabel", { label: tradeOff.optionB.label })}
                    </Typography>
                    <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                      {tradeOff.optionB.description}
                    </Typography>
                  </Paper>
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: alpha(levelColor, 0.04),
                    border: `1px dashed ${alpha(levelColor, 0.35)}`,
                    borderRadius: 2,
                    p: 1.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: levelColor, letterSpacing: "0.04em", textTransform: "uppercase" }}
                  >
                    {audience === "candidate" ? t("ScenarioPage.interviewersThinkLabel") : t("ScenarioPage.interviewerInsightLabel")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.65, color: "text.primary" }}>
                    {tradeOff.interviewerInsight}
                  </Typography>
                </Paper>
              </Paper>
            ))}
          </Stack>
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* ── Observability & evaluation ───────────────── */}
        <Section
          icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
          title={t("ScenarioPage.observabilityEvaluationTitle")}
        >
          <Prose text={scenario.observabilityEvaluation.narrative} />
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color: "text.secondary",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              display: "block",
              mt: 2,
              mb: 1.5,
            }}
          >
            {t("ScenarioPage.keyMetricsLabel")}
          </Typography>
          <Stack spacing={0.75}>
            {scenario.observabilityEvaluation.metrics.map((m, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: levelColor,
                    mt: "9px",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                  {m}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* ── Scalability & cost ───────────────────────── */}
        <Section
          icon={<TuneIcon sx={{ fontSize: 20 }} />}
          title={t("ScenarioPage.scalabilityCostTitle")}
        >
          <Prose text={scenario.scalabilityCost} />
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* ── Safety & governance ──────────────────────── */}
        <Section
          icon={<SecurityIcon sx={{ fontSize: 20 }} />}
          title={t("ScenarioPage.safetyGovernanceTitle")}
        >
          <Prose text={scenario.safetyGovernance} />
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* ── Level differentiation guide ──────────────── */}
        <Section
          icon={<GradeIcon sx={{ fontSize: 20 }} />}
          title={t("ScenarioPage.levelDifferentiationTitle")}
          subtitle={
            audience === "candidate"
              ? t("ScenarioPage.levelDifferentiationSubtitleCandidate")
              : t("ScenarioPage.levelDifferentiationSubtitleHm")
          }
        >
          <Stack spacing={1.5}>
            {scenario.levelDifferentiation.intermediate && (
              <LevelRow label={t("ScenarioPage.levelIntermediate")} text={scenario.levelDifferentiation.intermediate} color={theme.palette.success.main} />
            )}
            {scenario.levelDifferentiation.senior && (
              <LevelRow label={t("ScenarioPage.levelSenior")} text={scenario.levelDifferentiation.senior} color={theme.palette.info.main} />
            )}
            {scenario.levelDifferentiation.staff && (
              <LevelRow label={t("ScenarioPage.levelStaff")} text={scenario.levelDifferentiation.staff} color={theme.palette.primary.main} />
            )}
            {scenario.levelDifferentiation.principal && (
              <LevelRow label={t("ScenarioPage.levelPrincipal")} text={scenario.levelDifferentiation.principal} color={levelColor} />
            )}
          </Stack>
        </Section>

        {/* ── Prev/Next scenario nav ───────────────────── */}
        <Divider sx={{ my: 4 }} />
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
          {prevScenario ? (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/agentic-interviewing/scenario/${prevScenario.slug}`)}
              sx={{ textTransform: "none", justifyContent: "flex-start", textAlign: "left" }}
            >
              <Box>
                <Typography variant="caption" color="text.disabled" display="block">
                  {t("ScenarioPage.previousScenarioLabel")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {prevScenario.title}
                </Typography>
              </Box>
            </Button>
          ) : (
            <Box />
          )}
          {nextScenario && (
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(`/agentic-interviewing/scenario/${nextScenario.slug}`)}
              sx={{ textTransform: "none", justifyContent: "flex-end", textAlign: "right" }}
            >
              <Box>
                <Typography variant="caption" color="text.disabled" display="block">
                  {t("ScenarioPage.nextScenarioLabel")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {nextScenario.title}
                </Typography>
              </Box>
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Level row used in the differentiation guide
// ---------------------------------------------------------------------------

function LevelRow({ label, text, color }: { label: string; text: string; color: string }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${alpha(color, 0.3)}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 2,
        p: 2,
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
        <Chip
          label={label}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.72rem",
            letterSpacing: "0.04em",
            bgcolor: alpha(color, 0.12),
            color: color,
            minWidth: 90,
          }}
        />
        <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
          {text}
        </Typography>
      </Stack>
    </Paper>
  );
}
